import { api } from '@shared/api';
import type { CafeOrders, Recipe } from '../model/activities';

/**
 * Новая игра «Выживи в Лондоне», этап 1 «Мир без ИИ» (docs/product/game-stage1.md). Сервер хранит состояние и проверяет
 * каждое действие: деньги, сытость, силы и шаг сюжета считает он. Ответы — snake_case, как у старой игры.
 */

export type SceneId = 'street' | 'hostel' | 'cafe' | 'chippy';
export type Daypart = 'morning' | 'day' | 'evening' | 'night';

export interface Target {
  scene?: string;
  npc?: string;
  place?: string;
  spot?: string;
  shop?: string;
  action?: string;
  chat?: string;
}

export interface ContentScene {
  id: string;
  kind: 'street' | 'interior' | string;
  name_ru: string;
  name_en?: string;
}

export interface ContentNpc {
  id: string;
  name_ru: string;
  name_en: string;
  role_ru: string;
  scene: string;
  /** «с Каем» — для подписей кнопок. */
  with_ru?: string;
  gender?: 'm' | 'f';
  spot?: string;
  /** Тайна для контактов: сколько всего и настроение без темы (§ 6.1). */
  secret?: { total: number; mood_ru: string } | null;
}

export interface ShopItem {
  id: string;
  name_en: string;
  name_ru: string;
  price_pence: number;
  fullness: number;
  kind?: 'food' | 'drink';
}

export interface Shop {
  id: string;
  scene: string;
  npc?: string | null;
  title_en: string;
  title_ru?: string;
  /** Почему закрыто (кафе в 1-й день). Открыт ли сейчас — `state.open_shops`. */
  closed_ru?: string;
  items: ShopItem[];
}

export interface DialogOption {
  id: string;
  kind_ru?: string;
  en: string;
  ru: string;
  done?: string[];
  bonus?: string[];
  next?: string;
  end?: boolean;
  /** Своё прощание у варианта — вместо прощания разговора. */
  close?: DialogLine | null;
  know_ru?: string;
  /** Что персонаж почувствовал или сделал от этих слов (по-русски, от третьего лица). */
  reaction_ru?: string;
}

export interface DialogNode {
  emo?: number;
  npc_en: string;
  npc_ru: string;
  simple_en?: string;
  think_ru?: string;
  told_ru?: string;
  tag_ru?: string;
  options: DialogOption[];
  /** Запись реплики голосом жителя (ElevenLabs, кэш). */
  audio?: string | null;
}

export interface DialogLine {
  emo?: number;
  npc_en: string;
  npc_ru: string;
  audio?: string | null;
}

export interface Dialog {
  npc: string;
  scene: string;
  requires?: Record<string, unknown>;
  goal?: { title_ru: string; items: Record<string, string> } | null;
  bonus?: Record<string, string> | null;
  words?: [string, string][];
  start: string;
  nodes: Record<string, DialogNode>;
  close?: DialogLine | null;
  effects?: Record<string, unknown>;
  /** Подходит сама (Мия на 2-й день). */
  approach?: boolean;
  /** Реплика, если уйти посреди разговора. */
  wait?: DialogLine | null;
  result?: { title_ru?: string; opened_ru?: string[] } | null;
  skill_ru?: string;
}

export interface ChatReplyLine {
  en: string;
  ru: string;
  note_ru?: string;
}

export interface ChatOption {
  id: string;
  en: string;
  ru: string;
  /** Ответ собеседника на этот вариант; нет — общий `message.reply`. */
  reply?: ChatReplyLine | null;
}

export interface ChatMessage {
  id: string;
  day: number;
  from: string;
  en: string;
  ru: string;
  think_ru?: string;
  options?: ChatOption[];
  reply?: ChatReplyLine | null;
  /** Вечернее письмо бабушке: ответ приходит утром. */
  letter?: boolean;
}

export interface Chat {
  id: string;
  name_ru: string;
  messages: ChatMessage[];
}

export interface Step {
  id: string;
  goal_ru: string;
  /** Чем засчитывается: dialog / buy_in (+buy_kind) / chat / sleep / end. */
  done_when?: { dialog?: string; buy_in?: string; buy_kind?: string; chat?: string; sleep?: boolean; end?: boolean } | null;
  /** Повод из сюжета — почему эта цель (одна линия). */
  why_ru?: string;
  target?: Target | null;
  next?: string | null;
}

export interface Task {
  id: string;
  title_ru: string;
  from?: string;
  who_ru?: string;
  why_ru?: string;
  where_ru?: string;
  reward_ru?: string;
  locked?: boolean;
}

export interface IntroPage {
  photo: number;
  text_ru: string;
  cap_ru: string;
  questions?: [string, string][];
  fox_ru?: string;
}

export interface LondonContent {
  content_version: string;
  scenes: ContentScene[];
  npcs: ContentNpc[];
  shops: Shop[];
  dialogs: Record<string, Dialog>;
  chats: Chat[];
  steps: Step[];
  tasks: Task[];
  intro: IntroPage[];
  /** Дела без сил (механика 5, этап 3): рецепты бабушкиной книги и заказы мини-игры кафе. */
  recipes?: Recipe[];
  cafe_orders?: CafeOrders;
  /** «Сегодня в Лондоне» по дням (этап 3). */
  days?: DayCard[];
  /** Места с фото (механика 2). */
  flashbacks?: { id: string; scene: string; spot: string; title_ru: string; photo?: number }[];
}

export interface DayCard {
  day: number;
  weather_ru?: string;
  news_ru?: string;
  tips_ru?: string[];
  hook_ru?: string;
}

export interface Energy {
  left: number;
  max: number;
  bonus: number;
  /** Следующий тариф: у Max его нет. */
  next_plan?: { title: string; max: number; price_rub: number } | null;
}

export interface Goal {
  step: string;
  text_ru: string;
  target?: Target | null;
}

export interface ChatState {
  seen: number;
  replies: Record<string, string>;
  /** Личный текст сообщения (утренний ответ бабушки на письмо). */
  personal?: Record<string, { en: string; ru?: string | null; fixes?: TalkFix[] }>;
  /** Сколько сообщений чата уже доступно (условия считает сервер). */
  available?: number;
  /** Ответы своими словами по сообщениям: свой текст, поправки, ответ собеседника. */
  texts?: Record<string, { en: string; reply_en?: string | null; reply_ru?: string | null; fixes?: TalkFix[] }>;
}

export interface LondonState {
  version: number;
  content_version: string;
  day: number;
  daypart: Daypart;
  scene: SceneId;
  spot: string;
  hunger: number;
  money_pence: number;
  confidence: number;
  energy: Energy;
  intro_seen: boolean;
  flags: string[];
  met: string[];
  items: string[];
  tried: string[];
  dialogs_done: string[];
  tasks_done: string[];
  tracked_task: string | null;
  step: string;
  goal: Goal | null;
  chats: Record<string, ChatState>;
  day_log: Record<string, unknown>[];
  /** Что открыто сейчас — условия `requires` считает сервер. */
  open_dialogs?: string[];
  open_tasks?: string[];
  open_shops?: string[];
  /** Флешбэки, которые можно посмотреть сейчас (этап 3). */
  open_flashbacks?: string[];
  /** Найденные концовки. */
  endings?: string[];
  /** «Что я умею» — отработанные навыки общения (механика 4), по-русски. */
  skills_ru?: string[];
  /** Просмотренные флешбэки — попадают в альбом. */
  flashbacks_seen?: string[];
  chapter?: { id: string; no: number; title_ru: string } | null;
  /** Сколько игровых дней не заходил — для «Пока тебя не было». */
  away_days?: number;
  /** Имя героя, которое выбрал игрок (null — ещё не выбрано, в текстах «Alex»). */
  hero_name?: string | null;
  /** Подсказка имени из профиля латиницей («Алексей» → «Alexey»). */
  hero_name_suggest?: string | null;
  /** Что жители знают об игроке: как зовут (имя, которым представился) и факты о нём. */
  contacts?: Record<string, { calls_me: string | null; knows_ru: string[] }>;
  /** Непоказанные «Итог дня», открытка главы, утро — переживают перезагрузку; показал — `events_seen`. */
  pending_events?: LondonEvent[];
}

export type LondonAction =
  | { type: 'intro_done' }
  | { type: 'set_name'; name: string }
  | { type: 'events_seen'; kinds: string[] }
  | { type: 'move'; scene: SceneId; spot: string }
  | { type: 'dialog'; dialog_id: string; path: string[] }
  | { type: 'buy'; shop: string; item: string }
  | { type: 'chat_read'; chat: string }
  | { type: 'chat_reply'; chat: string; message: string; option: string }
  | { type: 'track'; task: string | null }
  | { type: 'sleep' }
  | { type: 'flashback'; id: string }
  | { type: 'activity'; kind: 'cook' | 'drink'; id: string; stars: number; perfect?: number }
  | { type: 'shift_start' }
  | { type: 'shift_done'; served: number; perfect: number };

export interface LondonEvent {
  kind: string;
  [key: string]: unknown;
}

export interface ActionResult {
  state: LondonState;
  events: LondonEvent[];
}

export async function fetchLondonContent(): Promise<LondonContent> {
  const { data } = await api.get<LondonContent>('/london/content');
  return data;
}

export async function fetchLondonState(): Promise<LondonState> {
  const { data } = await api.get<LondonState>('/london/state');
  return data;
}

export async function postLondonAction(action: LondonAction, clientVersion: number): Promise<ActionResult> {
  const { data } = await api.post<ActionResult>('/london/actions', { ...action, client_version: clientVersion });
  return data;
}

// ---------- Этап 2: разговоры с ИИ (docs/product/game-stage2.md) ----------

export type TalkMode = 'story' | 'free' | 'situation' | 'demo' | 'mission';

/** Подсказка к пункту задачи разговора (goal_help.json): почему этот пункт и готовая фраза. */
export interface TaskHelp {
  why_ru: string;
  say_en?: string;
  say_ru?: string;
}

export interface TalkLine {
  who: 'npc' | 'me';
  en: string;
  ru?: string | null;
  emo?: number | null;
  /** Что персонаж почувствовал от слов игрока (у его ответа). */
  reaction_ru?: string | null;
  /** Поправки к реплике игрока (при продолжении разговора). */
  fixes?: TalkFix[] | null;
  /** Запись реплики персонажа голосом ElevenLabs (сервер озвучивает лениво; нет — голос браузера). */
  audio?: string | null;
  /** Разбор произношения реплики игрока (ответ голосом). */
  pron?: TalkPron[] | null;
  /** Что житель сейчас делает — ремарка новеллы («Олли достаёт меню из-под прилавка»). */
  action_ru?: string | null;
}

/** Меню лавки, которое житель показал в разговоре. */
export interface TalkMenu {
  shop: string;
  title_en: string;
  title_ru: string;
  items: { id: string; name_en: string; name_ru: string; price_pence: number; kind: string }[];
}

/** Житель назвал цену (со скидкой, если уговорили). */
export interface TalkOffer {
  item_id: string;
  name_en: string;
  name_ru: string;
  qty: number;
  price_pence: number;
  list_price_pence: number;
}

/** Сделка прошла: деньги и сытость после неё (в демо — null). */
export interface TalkPurchase {
  item_id: string;
  name_en: string;
  name_ru: string;
  qty: number;
  price_pence: number;
  money_pence: number | null;
  fullness: number | null;
  fullness_delta: number | null;
}

export interface TalkHint {
  tactic_ru: string;
  en: string;
  ru: string;
}

export interface TalkFix {
  wrong: string;
  right: string;
  why_ru: string;
  lesson?: { slug: string; title_ru: string } | null;
}

/** Разбор произношения (только ответы голосом): что распознано, что имелось в виду, совет по звуку, запись слова. */
export interface TalkPron {
  heard: string;
  meant: string;
  tip_ru: string;
  audio?: string | null;
}

export interface Talk {
  talk_id: string;
  npc: string;
  mode: TalkMode;
  /** Разговор не тратит силы (пролог, бесплатные сюжетные) — строку «реплика = 1 сила» не показываем. */
  free?: boolean;
  task: { title_ru: string; items: Record<string, string>; help?: Record<string, TaskHelp> } | null;
  bonus: Record<string, string>;
  situation_ru: string | null;
  lines: TalkLine[];
  hints: TalkHint[];
  think_ru: string;
  energy: Energy;
  friendship: { level: number; name_ru: string };
  memory_ru: string | null;
  /** Уже отмеченные пункты (при продолжении после перезагрузки). */
  done?: string[];
  bonus_done?: string[];
}

export interface TalkTurn {
  me: TalkLine;
  line: TalkLine;
  done: string[];
  bonus_done: string[];
  fixes: TalkFix[];
  hints: TalkHint[];
  think_ru: string;
  energy: Energy;
  ended: boolean;
  close: TalkLine | null;
  over_limit: boolean;
  /** Что персонаж почувствовал от слов игрока: «Сэм смеётся — ей приятно, что спросили». */
  reaction_ru?: string | null;
  /** Ответ не по-английски: персонаж просит по-английски, ничего не засчитано и сил не потрачено. */
  not_english?: boolean;
  /** Разбор произношения (только ответ голосом). */
  pron?: TalkPron[];
  /** Все пункты задачи закрыты, но разговор продолжается — можно поговорить ещё или попрощаться (владелец 2026-11-03). */
  task_done?: boolean;
  /** Покупка словами (владелец 2026-11-03): ремарка, меню, предложение цены, сделка. */
  action_ru?: string | null;
  menu?: TalkMenu | null;
  offer?: TalkOffer | null;
  purchase?: TalkPurchase | null;
  /** Звания Граммозавра, полученные этим ответом (please, ночные разговоры). */
  new_titles?: { id: string; name_ru: string; line_ru: string }[];
}

export interface TalkSummary {
  done: string[];
  bonus: string[];
  fixes: TalkFix[];
  /** Разбор произношения за разговор (до 5). */
  pron?: TalkPron[];
  words: [string, string][];
  friendship_from: number;
  friendship_to: number;
  remembered_ru: string[];
  opened_ru: string[];
  /** Что Алекс узнал о персонаже. */
  learned_ru?: string[];
  goal: Goal | null;
  events: LondonEvent[];
  /** Прощальная реплика жителя на «Попрощаться» (с голосом); null — игрок молчал или житель уже попрощался. */
  close?: TalkLine | null;
}

/** Ответ модели бывает дольше 15 с общего таймаута клиента: ход на сервере записан, а игрок видел «проверь связь». */
const TALK_TIMEOUT = 60_000;

export async function startTalk(body: { npc: string; dialog_id?: string; situation_id?: string }): Promise<Talk> {
  const { data } = await api.post<Talk>('/london/talk/start', body, { timeout: TALK_TIMEOUT });
  return data;
}

export async function fetchActiveTalk(): Promise<Talk | null> {
  const { data } = await api.get<Talk | null>('/london/talk/active');
  return data ?? null;
}

export async function replyTalk(talkId: string, text: string): Promise<TalkTurn> {
  const { data } = await api.post<TalkTurn>(`/london/talk/${talkId}/reply`, { text }, { timeout: TALK_TIMEOUT });
  return data;
}

export async function replyTalkVoice(talkId: string, audio: Blob): Promise<TalkTurn> {
  const form = new FormData();
  const ext = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm';
  form.append('audio', audio, `reply.${ext}`);
  const { data } = await api.post<TalkTurn>(`/london/talk/${talkId}/reply`, form, { timeout: TALK_TIMEOUT });
  return data;
}

export async function byeTalk(talkId: string): Promise<TalkSummary> {
  const { data } = await api.post<TalkSummary>(`/london/talk/${talkId}/bye`, undefined, { timeout: TALK_TIMEOUT });
  return data;
}

export async function topupEnergy(): Promise<LondonState> {
  const { data } = await api.post<LondonState>('/london/energy/topup');
  return data;
}

export interface ChatReplyResult {
  reply_en: string | null;
  reply_ru: string | null;
  fixes: TalkFix[];
  state: LondonState;
  /** Письмо бабушке: ответ придёт утром. */
  letter?: boolean;
  events?: LondonEvent[];
}

export async function chatReplyText(chat: string, message: string, text: string): Promise<ChatReplyResult> {
  const { data } = await api.post<ChatReplyResult>('/london/chat/reply', { chat, message, text }, { timeout: TALK_TIMEOUT });
  return data;
}

// ---------- Архив разговоров — «Разборы» в телефоне ----------

export interface TalkBrief {
  talk_id: string;
  npc: string;
  day: number;
  mode: string;
  title_ru: string;
  done: number;
  total: number;
  fixes: number;
  /** Сколько замечаний по произношению. */
  pron?: number;
  words: number;
  ended_at?: string | null;
}

export interface TalkRecord {
  talk_id: string;
  npc: string;
  day: number;
  mode: string;
  title_ru: string;
  lines: { who: 'npc' | 'me'; en: string; ru?: string | null; emo?: number | null; reaction_ru?: string | null; fixes?: TalkFix[]; pron?: TalkPron[] }[];
  fixes: TalkFix[];
  pron?: TalkPron[];
  words: [string, string][];
  done?: string[];
  bonus?: string[];
  remembered_ru?: string[];
  learned_ru?: string[];
}

export async function fetchTalks(before?: string): Promise<TalkBrief[]> {
  const { data } = await api.get<TalkBrief[]>('/london/talks', { params: { limit: 50, ...(before ? { before } : {}) } });
  return data;
}

export async function fetchTalkDetail(id: string): Promise<TalkRecord> {
  const { data } = await api.get<TalkRecord>(`/london/talks/${encodeURIComponent(id)}`);
  return data;
}

// ---------- Демо без регистрации: короткая сцена «Олли в закусочной» (решение владельца 2026-11-03) ----------

export type DemoLevel = 'zero' | 'basic' | 'confident';

export async function startDemo(level?: DemoLevel): Promise<Talk> {
  const { data } = await api.post<Talk>('/london/demo/start', level ? { level } : {}, { timeout: TALK_TIMEOUT });
  return data;
}

export async function demoReply(id: string, text: string): Promise<TalkTurn> {
  const { data } = await api.post<TalkTurn>(`/london/demo/${id}/reply`, { text }, { timeout: TALK_TIMEOUT });
  return data;
}

export async function demoReplyVoice(id: string, audio: Blob): Promise<TalkTurn> {
  const form = new FormData();
  form.append('audio', audio, audio.type.includes('mp4') ? 'reply.mp4' : 'reply.webm');
  const { data } = await api.post<TalkTurn>(`/london/demo/${id}/reply`, form, { timeout: TALK_TIMEOUT });
  return data;
}

export async function demoBye(id: string): Promise<TalkSummary> {
  const { data } = await api.post<TalkSummary>(`/london/demo/${id}/bye`, undefined, { timeout: TALK_TIMEOUT });
  return data;
}

/** После регистрации: ошибки демо — в учёт ошибок (Граммозавр), слова — для карточек. Повтор безопасен. */
export async function claimDemo(demoId: string): Promise<{ words: [string, string][] }> {
  const { data } = await api.post<{ words: [string, string][] }>('/london/demo/claim', { demo_id: demoId });
  return data;
}

// ---------- Карточка героя «Что о тебе знают в Лондоне» (владелец 2026-11-03; game-stage3.md, «Карточка героя») ----------

/** Пункт карточки: увлечение, факт о герое, манера общения или тема, которая не заходит. */
export interface HeroCardItem {
  id: string;
  kind: 'interest' | 'fact' | 'style' | 'avoid';
  ru: string;
  day?: number;
}

export async function fetchHeroCard(): Promise<HeroCardItem[]> {
  const { data } = await api.get<{ items: HeroCardItem[] }>('/london/profile');
  return data.items ?? [];
}

/** Забыть пункт: жители перестают его помнить (сервер убирает похожее и из их памяти). */
export async function forgetHeroCardItem(id: string): Promise<HeroCardItem[]> {
  const { data } = await api.delete<{ items: HeroCardItem[] }>(`/london/profile/${encodeURIComponent(id)}`);
  return data.items ?? [];
}
